import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, ArrowRight, ShieldCheck, Zap, Star } from 'lucide-react';
import Header from '../components/Header';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';

const Auth = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialMode = location.pathname.endsWith('/register') ? 'register' : 'login';
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);
  const { signin, signup, startGoogleLogin } = useAuth();

  const onChange = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast({ title: 'Champs manquants', description: 'Merci de renseigner email et mot de passe.' });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      const res = mode === 'login' ? signin(form) : signup(form);
      setLoading(false);
      if (!res.ok) return toast({ title: 'Erreur', description: res.error });
      toast({ title: 'Bienvenue', description: `Ravi de vous revoir, ${res.user.name} !` });
      navigate('/account');
    }, 500);
  };

  return (
    <div>
      <AnnouncementBar />
      <Header />

      <div className="relative max-w-6xl mx-auto px-5 lg:px-8 py-14 lg:py-24 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <div className="text-[#ff8247] text-xs font-bold uppercase tracking-widest mb-3">Espace client</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-4">
            {mode === 'login' ? <>Retrouvez vos <span className="gradient-text">commandes</span></> : <>Créez votre <span className="gradient-text">compte</span></>}
          </h1>
          <p className="text-white/60 leading-relaxed mb-8 max-w-md">
            Suivez vos livraisons en temps réel, sauvegardez vos handles favoris et accédez à vos reçus en un clic.
          </p>

          <div className="space-y-3">
            {[
              { i: Zap, t: 'Suivi de livraison en temps réel', d: 'Voyez chaque étape de vos boosts.' },
              { i: ShieldCheck, t: 'Historique sécurisé', d: 'Vos commandes conservées en local, 100% privé.' },
              { i: Star, t: 'Support prioritaire', d: 'Réponse en moins de 30 min pour les clients.' }
            ].map((it, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#ff8247]/10 flex items-center justify-center flex-shrink-0"><it.i className="w-5 h-5 text-[#ff8247]" /></div>
                <div><div className="text-white font-semibold text-sm">{it.t}</div><div className="text-white/50 text-xs">{it.d}</div></div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-card rounded-3xl p-6 lg:p-8">
          <div className="flex gap-2 p-1 bg-white/5 rounded-full mb-6">
            <button onClick={() => setMode('login')}
              className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition ${mode === 'login' ? 'bg-white text-[#0a0a0f]' : 'text-white/60 hover:text-white'}`}>
              Connexion
            </button>
            <button onClick={() => setMode('register')}
              className={`flex-1 py-2.5 rounded-full text-sm font-semibold transition ${mode === 'register' ? 'bg-white text-[#0a0a0f]' : 'text-white/60 hover:text-white'}`}>
              Créer un compte
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <button
              type="button"
              onClick={startGoogleLogin}
              data-testid="google-signin-btn"
              className="w-full flex items-center justify-center gap-3 py-3 rounded-xl bg-white hover:bg-white/90 transition text-[#0a0a0f] font-semibold text-sm"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.2 29.4 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.7 29 5 24 5 13.5 5 5 13.5 5 24s8.5 19 19 19 19-8.5 19-19c0-1.2-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.9 18.9 13 24 13c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.7 29 5 24 5 16.3 5 9.6 9 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 43c5 0 9.5-1.7 13-4.6l-6-5.1C29.3 34.4 26.8 35 24 35c-5.4 0-9.9-2.8-11.4-6.9l-6.5 5C9.4 39 16.1 43 24 43z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.5l6 5.1C40.9 35.6 43 30.3 43 24c0-1.2-.1-2.3-.4-3.5z"/>
              </svg>
              Continuer avec Google
            </button>

            <div className="flex items-center gap-3 py-1">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-white/40 text-[10px] uppercase tracking-widest">ou</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            {mode === 'register' && (
              <div>
                <label className="text-white/60 text-xs mb-2 block">Prénom</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input value={form.name} onChange={onChange('name')} type="text" placeholder="Alex"
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-[#ff8247] transition" />
                </div>
              </div>
            )}
            <div>
              <label className="text-white/60 text-xs mb-2 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input value={form.email} onChange={onChange('email')} type="email" placeholder="vous@email.com" required
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-[#ff8247] transition" />
              </div>
            </div>
            <div>
              <label className="text-white/60 text-xs mb-2 block">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <input value={form.password} onChange={onChange('password')} type="password" placeholder="••••••••" required
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-[#ff8247] transition" />
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full justify-center inline-flex items-center gap-2 disabled:opacity-60">
              {loading ? 'Un instant…' : (mode === 'login' ? 'Se connecter' : 'Créer mon compte')}
              <ArrowRight className="w-4 h-4" />
            </button>

            <div className="text-center text-white/40 text-xs">
              {mode === 'login' ? (
                <>Pas encore de compte ? <button type="button" onClick={() => setMode('register')} className="text-[#ff8247] hover:underline">Créer un compte</button></>
              ) : (
                <>Déjà client ? <button type="button" onClick={() => setMode('login')} className="text-[#ff8247] hover:underline">Se connecter</button></>
              )}
            </div>
          </form>
        </div>
      </div>

      <Footer showCTA={false} />
    </div>
  );
};

export default Auth;
